import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { mandatoService, MandatoComNomes } from "../services/mandatoService";

/**
 * Hook para obter o mandato ativo da empresa selecionada.
 * Centraliza a lógica de busca do mandato ativo e suas datas.
 *
 * As tabs Dashboard e Cronograma devem usar este hook para obter
 * a data de término do mandato, ao invés de campos legados.
 */
export function useMandatoAtivo() {
  const { empresaSelecionada } = useAuth();
  const [mandatoAtivo, setMandatoAtivo] = useState<MandatoComNomes | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregarMandatoAtivo = useCallback(async () => {
    if (!empresaSelecionada?.id) {
      setMandatoAtivo(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(
        "🔄 [useMandatoAtivo] Buscando mandato ativo para empresa:",
        empresaSelecionada.id,
      );

      const mandato = await mandatoService.obterAtivo(empresaSelecionada.id);

      if (mandato) {
        console.log("✅ [useMandatoAtivo] Mandato ativo encontrado:", {
          id: mandato.id,
          numero: mandato.numero_mandato,
          inicio: mandato.data_inicio,
          fim: mandato.data_fim,
          status: mandato.status,
        });
      } else {
        console.log("⚠️ [useMandatoAtivo] Nenhum mandato ativo encontrado");
      }

      setMandatoAtivo(mandato);
    } catch (err) {
      console.error("❌ [useMandatoAtivo] Erro ao buscar mandato ativo:", err);
      setError(
        err instanceof Error ? err.message : "Erro ao buscar mandato ativo",
      );
      setMandatoAtivo(null);
    } finally {
      setLoading(false);
    }
  }, [empresaSelecionada?.id]);

  useEffect(() => {
    carregarMandatoAtivo();
  }, [carregarMandatoAtivo]);

  // Dados derivados para facilitar o uso
  const dataTerminoMandato = mandatoAtivo?.data_fim || null;
  const dataInicioMandato = mandatoAtivo?.data_inicio || null;
  const temMandatoAtivo = !!mandatoAtivo;

  // Formatadores de data
  const dataTerminoFormatada = dataTerminoMandato
    ? dataTerminoMandato.split("-").reverse().join("/")
    : "Não configurado";

  const dataInicioFormatada = dataInicioMandato
    ? dataInicioMandato.split("-").reverse().join("/")
    : "Não configurado";

  const periodoFormatado = temMandatoAtivo
    ? `${dataInicioFormatada} - ${dataTerminoFormatada}`
    : "Nenhum mandato ativo";

  return {
    mandatoAtivo,
    loading,
    error,
    recarregar: carregarMandatoAtivo,
    // Dados derivados
    dataTerminoMandato,
    dataInicioMandato,
    temMandatoAtivo,
    dataTerminoFormatada,
    dataInicioFormatada,
    periodoFormatado,
  };
}

export default useMandatoAtivo;
